import type { ReactNode } from "react";
import { SectionHeader } from "@/components/ui/SectionHeader";

/** Two-column list of included features, separated by hairlines. */
export function FeatureGrid({
  id,
  title,
  lead,
  items,
  footer,
  tone = "white",
}: {
  id: string;
  title: string;
  lead?: string;
  items: readonly { title: string; desc: string; note?: string }[];
  footer?: ReactNode;
  tone?: "white" | "bg";
}) {
  return (
    <section aria-labelledby={id} className={tone === "white" ? "bg-white" : "bg-brand-bg"}>
      <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 md:py-28 lg:px-8">
        <SectionHeader id={id} title={title} lead={lead} />
        <ul className="mt-14 grid gap-x-14 sm:grid-cols-2">
          {items.map((item, i) => (
            <li
              key={item.title}
              data-reveal=""
              style={{ ["--reveal-delay" as string]: `${(i % 2) * 70}ms` }}
              className="group/feat border-t border-brand-primary/10 py-6"
            >
              <h3 className="node text-lg font-semibold text-brand-primary transition-colors duration-300 group-hover/feat:text-teal-strong">
                {item.title}
              </h3>
              <p className="mt-1.5 pl-[1.5em] leading-relaxed text-ink-soft">
                {item.desc}
                {item.note ? <span className="text-ink-muted"> ({item.note})</span> : null}
              </p>
            </li>
          ))}
        </ul>
        {footer ? <div className="mt-10">{footer}</div> : null}
      </div>
    </section>
  );
}

/** Businesses a service is built for, as a quiet row of chips. */
export function AudienceBlock({ title, items, note }: { title: string; items: readonly string[]; note?: string }) {
  return (
    <section aria-label={title} className="bg-brand-bg">
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div data-reveal="" className="flex flex-col gap-6 lg:flex-row lg:items-center lg:gap-12">
          <h2 className="shrink-0 font-display text-2xl font-semibold text-brand-primary lg:max-w-xs">{title}</h2>
          <ul className="flex flex-wrap gap-2.5">
            {items.map((item) => (
              <li key={item} className="rounded-full border border-brand-primary/12 bg-white px-4 py-2 text-base font-medium text-brand-primary transition-[background-color,border-color,translate] duration-300 hover:border-brand-secondary hover:bg-teal-soft motion-safe:hover:-translate-y-0.5">
                {item}
              </li>
            ))}
          </ul>
        </div>
        {note ? <p className="mt-5 text-ink-muted">{note}</p> : null}
      </div>
    </section>
  );
}

/** A navy band carrying one strong statement. */
export function StatementBand({ title, body }: { title: ReactNode; body?: ReactNode }) {
  return (
    <section className="on-navy light-pool-navy relative bg-brand-primary text-white [--pool-x:15%] [--pool-y:50%]">
      <div className="mx-auto max-w-5xl px-4 py-20 text-center sm:px-6 md:py-28">
        <p
          data-reveal=""
          className="font-display text-[clamp(2rem,4.6vw,3.6rem)] font-semibold leading-[1.08] tracking-[-0.02em]"
        >
          {title}
        </p>
        {body ? (
          <p data-reveal="" className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-on-navy-muted">
            {body}
          </p>
        ) : null}
      </div>
    </section>
  );
}
